/**
 * Разделы Студии — фиксированный список путей `/studio/$section`, из макета
 * (`prototype/Prototype.dc.html`, боковая навигация Студии).
 *
 * Живёт в `shared`, а не внутри `pages/studio`, по двум причинам:
 * 1. Роутер (`app/router/routes/studio.ts`) валидирует параметр ДО того, как страница
 *    загрузится — а страница грузится лениво (`lazyRouteComponent`). Импортируй роутер
 *    список прямо из `pages/studio`, статический импорт утянул бы её код в основной бандл
 *    и свёл на нет разбиение по чанкам, которое ради `lazyRouteComponent` и затевалось.
 * 2. `pages/studio` не может импортировать из `app/router` — это импорт снизу вверх,
 *    FSD его запрещает и Steiger завалит сборку.
 * `shared` — единственный слой, легальный для обоих направлений импорта.
 */
export const STUDIO_SECTIONS = [
  // `scenarios` — это «Гостевой путь» целиком: маршрут новых броней, сценарии и заготовки. Раньше
  // это были три отдельных раздела (`scenarios`/`templates`/`policy`), и они разводили по разным
  // экранам вещи, которые менеджер делает одним заходом, — а главное, прятали связь «сохранил
  // версию» → «включил её для новых броней» (0.4).
  'scenarios',
  // Тексты для гостя — ДВА раздела, а не один. Прежний `content` держал три вида списком с
  // группами, и разница между ними («кто инициирует показ», 2.1) читалась только из подписи к
  // группе. Инструкцию гость открывает сам кнопкой, сообщение мы отправляем — это разная работа с
  // разными вопросами («что увидит на кнопке» против «когда это уйдёт»), и делать её в одном
  // списке значит каждый раз выбирать вид, прежде чем начать.
  //
  // Документов на согласие здесь нет намеренно: гостю их не показывает ни один сервис (2.1).
  // Контракт и данные на месте — вернём раздел, когда их станет чем показать.
  'instructions',
  'messages',
  'brand',
  'apartments',
  'clients',
  'channels',
  'payments',
  'sources',
  'concierge',
  'audit',
] as const

export type StudioSectionSlug = (typeof STUDIO_SECTIONS)[number]

export function isStudioSection(value: string): value is StudioSectionSlug {
  return (STUDIO_SECTIONS as readonly string[]).includes(value)
}

/**
 * В каком разделе правится текст этого вида. Нужна не странице, а тем, кто ссылается на текст
 * ИЗВНЕ — шаг сценария открывает памятку и сообщение одной кнопкой, а разделы у них теперь разные.
 *
 * `null` — вида нет в интерфейсе. Сегодня это `agreement`: гостю документ не показывает ни один
 * сервис, и раздела для него мы не держим. Ссылаться на такой текст шаг всё ещё может (данные и
 * контракт на месте), поэтому вызывающий обязан разобрать `null`, а не открывать наугад.
 */
export function studioSectionForContentKind(kind: string): StudioSectionSlug | null {
  if (kind === 'instruction') return 'instructions'
  if (kind === 'message') return 'messages'
  return null
}
